import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import marketShopper from "@/assets/island-moments/market-shopper.jpg";
import yapheneGiftBag from "@/assets/island-moments/yaphene-gift-bag.jpg";
import balmBondWorkshop from "@/assets/island-moments/balm-bond-workshop.jpg";
import groupJars from "@/assets/island-moments/group-jars.jpg";
import outdoorBooth from "@/assets/island-moments/outdoor-booth.jpg";
import skelecDemo from "@/assets/island-moments/skelec-demo.jpg";
import skelecTasting from "@/assets/island-moments/skelec-tasting.jpg";
import picnicTasting from "@/assets/island-moments/picnic-tasting.jpg";
import marketWineHandoffAsset from "@/assets/island-moments/market-wine-handoff.jpg.asset.json";
import giftBagDeliveryAsset from "@/assets/island-moments/gift-bag-delivery.jpg.asset.json";

const marketWineHandoff = marketWineHandoffAsset.url;
const giftBagDelivery = giftBagDeliveryAsset.url;

interface Tile {
  src: string;
  alt: string;
  caption: string;
  /** Tailwind/inline classes controlling absolute placement on md+ */
  positionClass: string;
  /** Optional inline style for fine-tuned z/translate to create overlap */
  style?: React.CSSProperties;
  /** Mobile fallback aspect */
  mobileAspect: string;
}

/**
 * Editorial staggered collage. On md+ we use absolute positioning over a
 * fixed-height stage to achieve true magazine rhythm with intentional
 * overlap. On mobile, tiles stack as a clean single-column gallery.
 */
const tiles: Tile[] = [
  // 1. Dominant hero — left, oversized, taller than anything else
  {
    src: marketShopper,
    alt: "Customer with orange bag shopping handcrafted Sugar Town Organics goods at an outdoor booth in St. Kitts",
    caption: "Market Moments",
    positionClass: "md:absolute md:left-0 md:top-0 md:w-[44%] md:h-[760px]",
    style: { zIndex: 3 },
    mobileAspect: "aspect-[3/4]",
  },
  // 2. Upper right portrait — customer holding Yaphene bag
  {
    src: yapheneGiftBag,
    alt: "Customer smiling while holding a Yaphene gift bag at an island event",
    caption: "Island Gifting",
    positionClass: "md:absolute md:left-[46%] md:top-[10px] md:w-[24%] md:h-[330px]",
    style: { zIndex: 4 },
    mobileAspect: "aspect-[4/5]",
  },
  // 3. Right wide landscape — Balm & Bond seated workshop
  {
    src: balmBondWorkshop,
    alt: "Women gathered around a table inside Di Organics Shoppe for a Balm & Bond workshop",
    caption: "Workshop Gatherings",
    positionClass: "md:absolute md:left-[71%] md:top-[40px] md:w-[29%] md:h-[300px]",
    style: { zIndex: 2 },
    mobileAspect: "aspect-[16/10]",
  },
  // 4. Mid-right tile — SKELEC tasting (overlaps slightly with portrait)
  {
    src: skelecTasting,
    alt: "Hands-on botanical tasting and demo at a SKELEC women's session",
    caption: "Hands-On Learning",
    positionClass: "md:absolute md:left-[46%] md:top-[358px] md:w-[26%] md:h-[230px]",
    style: { zIndex: 5, transform: "translateX(-10px)" },
    mobileAspect: "aspect-[4/3]",
  },
  // 5. Mid-far-right — outdoor browsing (slight overlap on left)
  {
    src: outdoorBooth,
    alt: "Customer browsing Sugar Town Organics products at an outdoor island booth",
    caption: "Market Moments",
    positionClass: "md:absolute md:left-[73%] md:top-[358px] md:w-[27%] md:h-[230px]",
    style: { zIndex: 3 },
    mobileAspect: "aspect-[4/3]",
  },
  // 6. Bottom-left mid — group jars (overlaps the hero by ~10px)
  {
    src: groupJars,
    alt: "Group photo of women holding handcrafted jars after a wellness session in St. Kitts",
    caption: "Shared Experiences",
    positionClass: "md:absolute md:left-[40%] md:top-[610px] md:w-[32%] md:h-[260px]",
    style: { zIndex: 4, transform: "translateX(-12px) translateY(-8px)" },
    mobileAspect: "aspect-[16/10]",
  },
  // 7. Bottom-right — SKELEC demo
  {
    src: skelecDemo,
    alt: "Sugar Town Organics teaching a botanical demo at a SKELEC community session",
    caption: "Hands-On Learning",
    positionClass: "md:absolute md:left-[71%] md:top-[610px] md:w-[29%] md:h-[260px]",
    style: { zIndex: 2 },
    mobileAspect: "aspect-[4/3]",
  },
  // 8. Bottom-left small — picnic tasting (tucked under hero)
  {
    src: picnicTasting,
    alt: "Outdoor picnic tasting table with botanical brushes and island treats",
    caption: "Shared Experiences",
    positionClass: "md:absolute md:left-[6%] md:top-[780px] md:w-[32%] md:h-[200px]",
    style: { zIndex: 6, transform: "translateY(-12px)" },
    mobileAspect: "aspect-[4/3]",
  },
  // 9. Lower band left — market wine hand-off
  {
    src: marketWineHandoff,
    alt: "Two women exchanging a bottle of Flauriel pineapple wine at a Sugar Town Organics market booth in St. Kitts",
    caption: "Market Moments",
    positionClass: "md:absolute md:left-[40%] md:top-[1000px] md:w-[28%] md:h-[360px]",
    style: { zIndex: 4 },
    mobileAspect: "aspect-[3/4]",
  },
  // 10. Lower band right — gift bag delivery
  {
    src: giftBagDelivery,
    alt: "Customer smiling while holding a Sugar Town Organics gift bag beside a car in St. Kitts",
    caption: "Island Gifting",
    positionClass: "md:absolute md:left-[69%] md:top-[1000px] md:w-[28%] md:h-[360px]",
    style: { zIndex: 3 },
    mobileAspect: "aspect-[3/4]",
  },
];

const IslandMoments = () => {
  return (
    <section
      id="island-moments"
      aria-labelledby="island-moments-heading"
      className="relative py-[70px] md:py-[110px]"
      style={{ backgroundColor: "#faf7f2" }}
    >
      <div className="container mx-auto px-6" style={{ maxWidth: "1480px" }}>
        {/* Top copy block */}
        <Reveal className="mx-auto max-w-[860px] text-center">
          <p className="text-[11px] tracking-[0.36em] uppercase font-sans text-accent/90 mb-6">
            Seen, Loved &amp; Shared from St. Kitts
          </p>
          <h2
            id="island-moments-heading"
            className="font-serif text-[2.3rem] sm:text-[3rem] md:text-[3.75rem] leading-[1.05] tracking-[-0.01em] text-foreground mb-7"
          >
            A Living Island Brand <em className="not-italic text-primary">People Actually Experience</em>
          </h2>
          <p className="font-sans text-base md:text-[1.08rem] leading-relaxed text-muted-foreground">
            From shoppers discovering handcrafted finds at our market tables to
            women gathering for wellness workshops inside Di Organics Shoppe,
            Sugar Town Organics is more than an online store — it is an island
            experience shared, gifted, tasted and remembered.
          </p>
          <div className="mt-9">
            <Link
              to="/about-our-island"
              className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-9 py-3.5 rounded-full font-sans font-medium text-[11px] uppercase tracking-[0.3em] hover:bg-accent/90 hover:-translate-y-0.5 transition-all shadow-[0_14px_34px_-14px_rgba(180,130,40,0.55)]"
            >
              Explore Our Island Story
            </Link>
          </div>
        </Reveal>

        {/* Editorial staggered collage */}
        <Reveal delay={120} className="mt-14 md:mt-[64px]">
          {/* Mobile: clean stack. Desktop: absolute-positioned magazine layout */}
          <div className="grid grid-cols-1 gap-4 md:hidden">
            {tiles.map((tile, i) => (
              <Tile key={i} tile={tile} mobile />
            ))}
          </div>
          <div
            className="hidden md:block relative w-full"
            style={{ height: "1400px" }}
          >
            {tiles.map((tile, i) => (
              <Tile key={i} tile={tile} />
            ))}
          </div>
        </Reveal>

        <Reveal delay={180} className="mt-12 md:mt-16 text-center">
          <p className="font-serif italic text-sm md:text-base text-muted-foreground tracking-wide">
            Real People. Real Connections. Real Island Life.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const Tile = ({ tile, mobile = false }: { tile: Tile; mobile?: boolean }) => (
  <figure
    className={`group relative overflow-hidden rounded-[18px] shadow-[0_24px_50px_-24px_rgba(60,40,15,0.4)] ring-1 ring-black/5 ${
      mobile ? tile.mobileAspect : tile.positionClass
    }`}
    style={mobile ? undefined : tile.style}
  >
    <img
      src={tile.src}
      alt={tile.alt}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
      style={{ filter: "saturate(0.95) contrast(1.02)" }}
    />
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "linear-gradient(180deg, rgba(60,40,15,0.04) 0%, rgba(60,40,15,0.22) 100%)",
      }}
    />
    <figcaption className="absolute left-4 bottom-4 right-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
      <span className="inline-block bg-background/85 backdrop-blur-sm text-foreground text-[10px] tracking-[0.28em] uppercase font-sans px-3 py-1.5 rounded-full">
        {tile.caption}
      </span>
    </figcaption>
  </figure>
);

export default IslandMoments;
